// What the phone knows about itself.
//
// Nothing here identifies an attendee, and nothing ever should. The app has no user
// and must not hold attendee details on a shared device.
// What a phone was handed out to do. A partner's phone at a sponsored area records
// leads; one of ours on a workshop door decides who gets in. The screens, and what the
// phone is allowed to ask, differ.
export type ScanDevicePurpose = 'lead_capture' | 'access_control'

export interface ScanDeviceState {
  provisioned: boolean
  // Stands in for a login on every request. Held against this address only and
  // never as a cookie shared with the other WeAreDevelopers sites.
  token: string | null
  // Which pairing this phone is on now, made when the setup code is exchanged. The
  // server has no field that differs when the same partner pairs the same phone again.
  pairingId: string
  purpose: ScanDevicePurpose
  partnerName: string
  deviceLabel: string
  // Kept on the phone because uploading needs it, including a backlog drained after
  // a reload with no connection to ask again.
  eventId: number | null
  // The event's zone, e.g. America/Los_Angeles, which every time on screen is formatted
  // in. Empty on a phone paired before the server sent it, until its next online open.
  eventTimeZone: string
  areaId: number | null
  areaName: string
  // The door being worked, on an access control phone. Chosen per shift the same way
  // an area is, because a room changes workshop through the day.
  sessionId: number | null
  sessionTitle: string
  sessionRoom: string
  // The day, kept separate from the window because workshops run on every day of
  // the event and a clock time alone does not identify a sitting.
  sessionDate: string
  // Kept as the already formatted window rather than the raw timestamps. It is only ever
  // displayed, and formatting it once at selection keeps the door screen from depending
  // on the schedule being loaded.
  sessionTime: string

  // The last list the server gave this phone: the partner's areas, or the doors a phone
  // of ours may work.
  //
  // Remembered for one case, which is the one that matters most. Every app open asks
  // which area or door the shift is on, and that list comes from the server. A phone
  // opened with no connection therefore had no list, no way to answer the question, and
  // no way to scan — on an app whose whole point is that scanning survives the venue
  // WiFi. Remembering the list is what closes that.
  //
  // Neither list says anything about an attendee, so this holds nothing the design keeps
  // off a shared phone. A deactivated phone is cleared down to its pairing id, these
  // included.
  knownAreas: ScanArea[]
  knownSessions: DoorSession[]
}

// Written to the phone's own storage under this address. A phone is paired once
// and must stay paired, including after iOS discards the app from memory or the
// app updates itself.
export const SCAN_DEVICE_STORAGE_KEY = 'scan-device'

export const emptyScanDevice = (): ScanDeviceState => ({
  provisioned: false,
  token: null,
  // No pairing until a code is exchanged, and nothing may be uploaded against it.
  pairingId: '',
  // The safe default for a phone we know nothing about yet: the purpose that holds no
  // entitlement data and can only record.
  purpose: 'lead_capture',
  partnerName: '',
  deviceLabel: '',
  eventId: null,
  eventTimeZone: '',
  areaId: null,
  areaName: '',
  sessionId: null,
  sessionTitle: '',
  sessionRoom: '',
  sessionDate: '',
  sessionTime: '',
  knownAreas: [],
  knownSessions: [],
})

export const useScanDevice = () => useState<ScanDeviceState>('scan-device', emptyScanDevice)

/**
 * Where a paired phone belongs: the picker for what it was handed out to do.
 *
 * One definition, because this decision is made in five places (the global middleware,
 * the setup screen after pairing, and the guard on each of the four screens). A door
 * phone that lands on the area list looks exactly like a broken device to whoever is
 * holding it, and that is what happens the moment one of those places disagrees.
 */
export const scanDeviceHome = (purpose: ScanDevicePurpose): string =>
  purpose === 'access_control' ? '/workshops' : '/areas'
